import React, { useState } from "react";
import { Order, OrderWithGift } from "../../../api/order";
import DSDialog from "../../system-design/DSDialog";
import DSDialogTitle from "../../system-design/DSDialogTitle";
import DSDialogContent from "../../system-design/DSDialogContent";
import DSDialogActions from "../../system-design/DSDialogActions";
import DSBox from "../../system-design/DSBox";
import DSSecondaryButton from "../../system-design/DSSecondaryButton";
import useUpdateOrderManualOverrideMutation from "../hooks/useUpdateOrderManualOverrideMutation";
import DeviceModal from "../../Device/components/DeviceModal";
import CollectionDialog from "../../Collection/components/CollectionDialog";
import DSButton from "../../system-design/DSButton";
import adminApiClient from "../../../api/admin";
import { Device } from "../../../Types";
import OrderBasicInfo from "./OrderBasicInfo";
import OrderCollectionsSection from "./OrderCollectionsSection";
import OrderClocksSection from "./OrderClocksSection";
import OrderGiftSection from "./OrderGiftSection";
import OrderCommentSection from "./OrderCommentSection";
import useOrderQuery from "../hooks/useOrderQuery";

interface OrderModalProps {
  show: boolean;
  order: Order | OrderWithGift | null;
  onClose: () => void;
}

const OrderModal = ({ show, order: orderProp, onClose }: OrderModalProps) => {
  const { mutate: updateManualOverride } = useUpdateOrderManualOverrideMutation(
    orderProp?.id || "",
  );
  const [selectedClock, setSelectedClock] = useState<Device | null>(null);
  const [selectedCollectionId, setSelectedCollectionId] = useState<
    string | null
  >(null);
  // Keep the modal in sync with clock attach/detach mutations — the row
  // object from the list is a snapshot and never updates.
  const { data: fetchedOrder } = useOrderQuery(orderProp?.id || "");
  const order = fetchedOrder || orderProp;

  if (!order) return null;

  const gift = "gift" in order ? (order as OrderWithGift).gift : undefined;

  const handleClockClick = async (clockId: string) => {
    try {
      const clock = await adminApiClient.getClock(clockId);
      setSelectedClock(clock);
    } catch (error) {
      console.error("Failed to fetch clock:", error);
    }
  };

  const handleCollectionClick = (collectionId: string) => {
    setSelectedCollectionId(collectionId);
  };

  return (
    <>
      <DSDialog open={show} onClose={onClose} maxWidth="lg" fullWidth>
        <DSDialogTitle
          variant="h5"
          sx={{ borderBottom: "1px solid", borderBottomColor: "divider" }}
        >
          Order Details - {order.orderName}
        </DSDialogTitle>
        <DSDialogContent>
          <DSBox sx={{ p: 2 }}>
            <OrderBasicInfo
              order={order}
              onManualOverrideChange={updateManualOverride}
            />

            <OrderClocksSection order={order} onClockClick={handleClockClick} />

            <OrderCollectionsSection
              collections={order.collections}
              onCollectionClick={handleCollectionClick}
            />

            {gift && <OrderGiftSection gift={gift} />}

            <OrderCommentSection order={order} />
          </DSBox>
        </DSDialogContent>
        <DSDialogActions>
          {gift && (
            <DSButton
              onClick={() =>
                window.open(
                  `https://studio.klydoclock.com/gift?orderName=${order.orderName}`,
                  "_blank",
                )
              }
            >
              Watch Gift
            </DSButton>
          )}
          <DSSecondaryButton onClick={onClose}>Close</DSSecondaryButton>
        </DSDialogActions>
      </DSDialog>
      {selectedClock && (
        <DSDialog
          open={!!selectedClock}
          onClose={() => setSelectedClock(null)}
          maxWidth="lg"
          fullWidth
        >
          <DeviceModal
            setShowModal={() => setSelectedClock(null)}
            row={selectedClock}
          />
        </DSDialog>
      )}
      {selectedCollectionId && (
        <CollectionDialog
          collectionId={selectedCollectionId}
          onClose={() => setSelectedCollectionId(null)}
          open={!!selectedCollectionId}
          maxWidth="lg"
          fullWidth
        />
      )}
    </>
  );
};

export default OrderModal;
